import {
  Ban,
  CalendarDays,
  CheckCircle2,
  Edit3,
  Eye,
  Layers3,
  Loader2,
} from 'lucide-react';

import ActionButton from '../../../../components/Buttons/ActionButton';
import { RestaurantPurchaseOrderStatus } from '../../../../types/restaurant/RestaurantPurchaseOrderTypes';
import { formatMoney } from '../../../../utils/decimalValue';
import { PurchaseOrderItem } from '..';
import { isRestaurantPurchasePaymentComplete } from '../restaurantPurchasePayment';


interface PurchaseListProps {
  purchases: PurchaseOrderItem[];
  actingId: number | null;
  onView: (purchase: PurchaseOrderItem) => void;
  onEdit: (purchase: PurchaseOrderItem) => void;
  onConfirm: (purchase: PurchaseOrderItem) => void;
  onCancel: (purchase: PurchaseOrderItem) => void;
}

const statusInfo: Record<
  RestaurantPurchaseOrderStatus,
  { label: string; classes: string }
> = {
  DRAFT: {
    label: 'Borrador',
    classes:
      'bg-warning/10 text-warning dark:bg-warning/15',
  },
  CONFIRMED: {
    label: 'Confirmada',
    classes:
      'bg-success/10 text-success dark:bg-success/15',
  },
  CANCELLED: {
    label: 'Cancelada',
    classes:
      'bg-danger/10 text-danger dark:bg-danger/15',
  },
};

const formatPurchaseDate = (value: string) =>
  new Date(`${value}T00:00:00`).toLocaleDateString('es-SV');

const PurchaseList = ({
  purchases,
  actingId,
  onView,
  onEdit,
  onConfirm,
  onCancel,
}: PurchaseListProps) => {
  return (
    <div className="grid grid-cols-1 gap-4 p-5 sm:px-7 md:grid-cols-2 lg:hidden">
      {purchases.map((purchase) => {
        const acting = actingId === purchase.id;
        const canConfirm = isRestaurantPurchasePaymentComplete(
          purchase.purchaseDate,
          purchase.paymentCondition
        );

        return (
          <article
            key={purchase.id}
            className="rounded-xl border border-stroke bg-white p-5 shadow-card-2 dark:border-strokedark dark:bg-boxdark dark:shadow-none"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  {purchase.purchaseNumber}
                </p>

                <h3 className="mt-1 font-semibold text-black dark:text-white">
                  {purchase.supplierName}
                </h3>
              </div>

              <span
                className={`inline-flex shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                  statusInfo[purchase.status].classes
                }`}
              >
                {statusInfo[purchase.status].label}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-gray-2 p-3 dark:bg-meta-4/60">
                <p className="flex items-center gap-1.5 text-xs text-bodydark2">
                  <CalendarDays className="h-3.5 w-3.5" />
                  Fecha
                </p>

                <p className="mt-1 font-medium text-black dark:text-white">
                  {formatPurchaseDate(purchase.purchaseDate)}
                </p>
              </div>

              <div className="rounded-lg bg-gray-2 p-3 dark:bg-meta-4/60">
                <p className="flex items-center gap-1.5 text-xs text-bodydark2">
                  <Layers3 className="h-3.5 w-3.5" />
                  Detalles
                </p>

                <p className="mt-1 font-medium text-black dark:text-white">
                  {purchase.detailCount}
                </p>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-stroke pt-4 dark:border-strokedark">
              <span className="text-sm text-bodydark2">
                Total
              </span>

              <strong className="text-lg text-black dark:text-white">
                {formatMoney(purchase.total)}
              </strong>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <ActionButton
                label="Ver detalle"
                icon={<Eye className="h-4 w-4" />}
                variant="secondary"
                size="sm"
                fullWidth
                disabled={acting}
                onClick={() => onView(purchase)}
              />

              {purchase.status === 'DRAFT' && (
                <ActionButton
                  label="Editar"
                  icon={<Edit3 className="h-4 w-4" />}
                  variant="info"
                  size="sm"
                  fullWidth
                  disabled={acting}
                  onClick={() => onEdit(purchase)}
                />
              )}

              {purchase.status === 'DRAFT' && (
                <>
                  <ActionButton
                    label={acting ? 'Procesando' : 'Confirmar'}
                    icon={
                      acting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4" />
                      )
                    }
                    variant="success"
                    size="sm"
                    fullWidth
                    disabled={acting || !canConfirm}
                    onClick={() => onConfirm(purchase)}
                  />

                  <ActionButton
                    label="Cancelar"
                    icon={<Ban className="h-4 w-4" />}
                    variant="danger"
                    size="sm"
                    fullWidth
                    disabled={acting}
                    onClick={() => onCancel(purchase)}
                  />
                </>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
};

export default PurchaseList;
